/**
 * D-220: system text size in the iPhone and Android apps. The Android WebView applies the system font scale itself
 * (textZoom, up to 2.0x) on top of fixed layouts, and the iOS WKWebView ignores Dynamic Type. So the shell takes the
 * scale over: read what the person chose, put the WebView back to 100%, and hand the page a CAPPED scale as
 * `--user-text-scale` on <html>. base.css multiplies the root font size by it; page sizes are in rem, so text grows
 * (never past 1.3x) while the table, cards, hand and seats keep their pixel geometry. In a browser nothing is set: the
 * root font size is the browser's own, so Chrome's text size and Safari's page zoom work through rem alone.
 * An older store build without the @capacitor/text-zoom plugin rejects the calls; then the page does nothing here.
 */
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { TextZoom } from "@capacitor/text-zoom";

export const TEXT_SCALE_CAP = 1.3;
export const TEXT_SCALE_FLOOR = 0.85;
const VAR = "--user-text-scale";

/** The OS scale limited to what the pages are laid out for; anything unreadable counts as 1. */
export function clampTextScale(v: unknown): number {
  const n = typeof v === "number" ? v : Number.NaN;
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.round(Math.min(TEXT_SCALE_CAP, Math.max(TEXT_SCALE_FLOOR, n)) * 100) / 100;
}

/** Reads the OS preference, takes the Android WebView back to 100%, sets the root variable. Returns the scale used (null: left alone). */
export async function syncTextScale(): Promise<number | null> {
  const root = document.documentElement;
  if (!Capacitor.isNativePlatform()) return null;
  let preferred: number;
  try { preferred = (await TextZoom.getPreferred()).value; } catch { root.style.removeProperty(VAR); return null; }
  if (Capacitor.getPlatform() === "android") {
    // If the WebView can't be reset, don't also scale the page: that would apply the setting twice.
    try { await TextZoom.set({ value: 1 }); } catch { root.style.removeProperty(VAR); return null; }
  }
  const scale = clampTextScale(preferred);
  root.style.setProperty(VAR, String(scale));
  return scale;
}

/** On load and whenever the app comes back to the front (the person may have changed the setting meanwhile). */
export function watchTextScale(): void {
  if (!Capacitor.isNativePlatform()) return;
  void syncTextScale();
  try { void App.addListener("resume", () => { void syncTextScale(); }).catch(() => { /* not fatal */ }); } catch { /* not fatal */ }
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") void syncTextScale(); });
}
